import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  getFirestore, 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  query, 
  where,
  updateDoc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const AllEducatorMath = () => {
  const navigate = useNavigate();
  const [popupContent, setPopupContent] = useState(null);
  const [visibleTopics, setVisibleTopics] = useState(() => {
    const savedVisibility = localStorage.getItem("visibleTopicsMath");
    return savedVisibility ? JSON.parse(savedVisibility) : Array(27).fill(true);
  });
  
  const [formVisible, setFormVisible] = useState(false);
  const [educatorControlsVisible, setEducatorControlsVisible] = useState(false);
  const [students, setStudents] = useState([]);
  const [user, setUser] = useState(null);
  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
          setUser({ ...userSnap.data(), uid: currentUser.uid });
        } else {
          console.error("No such user found in Firestore!");
        }
      } else {
        console.error("No user is signed in.");
      }
    });

    return () => unsubscribe();
  }, [auth, db]);

  useEffect(() => {
    if (!user?.uid) return; // Ensure user UID exists before making a request
  
    const fetchEducatorSettings = async () => {
      try {
        const userDocRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userDocRef);
  
        if (userSnap.exists()) {
          const userData = userSnap.data();
          
          // Ensure visibility settings are retrieved from Firestore
          const storedVisibility = userData.matools?.map(tool => tool.visible) || Array(27).fill(true);
          setVisibleTopics(storedVisibility);
  
          // Sync with localStorage for persistence across sessions
          localStorage.setItem("visibleTopicsMath", JSON.stringify(storedVisibility));
        } else {
          console.error("Educator settings not found!");
        }
      } catch (error) {
        console.error("Error fetching educator settings:", error);
      }
    };
  
    fetchEducatorSettings();
  }, [user]); // Runs when the user state updates
  

  const handleCheckboxChange = (index) => {
    const updatedVisibleTopics = [...visibleTopics];
    updatedVisibleTopics[index] = !updatedVisibleTopics[index];
    setVisibleTopics(updatedVisibleTopics);
    localStorage.setItem("visibleTopicsMath", JSON.stringify(updatedVisibleTopics));
  };

  const handleSaveChanges = async () => {
    if (!user || !user.uid) {
      console.error("User UID is not defined.");
      return;
    }
  
    if (!topics || !Array.isArray(topics)) {
      console.error("Topics is not defined or not an array.");
      return;
    }
  
    const updatedTools = topics.map((topic, index) => ({
      title: topic.title,
      visible: visibleTopics[index] ?? true,  // Ensure visibleTopics[index] exists
    }));
  
    try {
      const userDocRef = doc(db, "users", user.uid); // Ensure user.uid is defined
      await updateDoc(userDocRef, { matools: updatedTools });
      alert("Changes saved successfully!");
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };
  

  const handleOpenPopup = (content) => {
    setPopupContent(content);
  };

  const handleClosePopup = () => {
    setPopupContent(null);
  };

  const handleHomeClick = () => {
    if (user) {
      const role = user.role.toLowerCase();
      if (role.includes("student")) {
        navigate("/dashboard");
      } else if (role.includes("educator")) {
        navigate("/educator/dashboard");
      } else if (role.includes("admin") || role.includes("administrator")) {
        navigate("/admin");
      } else {
        console.error("Unknown user role:", role);
      }
    } else {
      console.error("User is not defined.");
    }
  };

  const handleSubjectClick = (subject) => {
    const path = `/educator/all-all-educator-${subject}`;
    navigate(path);
  };

  const handleRequestClick = () => {
    setFormVisible(true);
  };

  const handleFormClose = () => {
    setFormVisible(false);
  };

  const handleEducatorControlsClick = () => {
    setEducatorControlsVisible(true);
  };

  const handleEducatorControlsClose = () => {
    setEducatorControlsVisible(false);
  };

  const handleStudentProgressClick = async () => {
    if (!user || !user.educatorCode) {
      console.error("No logged-in user or educator code found.");
      return;
    }
  
    try {
      const q = query(
        collection(db, "users"),
        where("role", "==", "nc-2nd-student"),
        where("educatorCode", "==", user.educatorCode)
      );
  
      const querySnapshot = await getDocs(q);
      const matchingUsers = querySnapshot.docs.map(doc => doc.data());
  
      setPopupContent(
        <div>
          <h2 style={{ color: "black" }}>Student Progress</h2>
          {user && (
            <>
              <h2 style={{ color: "black" }}>{user.sirName} {user.firstName} {user.lastName}'s Class</h2>
              <h3>Educator Code: {user.educatorCode}</h3>
            </>
          )}
          <h2>Student List</h2>
          <div style={styles.studentButtons}>
            {matchingUsers.length === 0 ? (
              <p>No students found with the specified criteria.</p>
            ) : (
              matchingUsers.map((student, index) => (
                <button
                  key={index}
                  style={{
                    ...styles.studentButton,
                    backgroundColor: `hsl(${(index * 40) % 360}, 70%, 80%)`,
                  }}
                  onClick={() => handleStudentClick(student.firstName)}
                >
                  {student.firstName}
                </button>
              ))
            )}
          </div>
        </div>
      );
    } catch (error) {
      console.error("Error fetching students:", error);
      setPopupContent(<p>Error loading students.</p>);
    }
  };

  const handleStudentClick = (firstName) => {
    alert(`It's me ${firstName}!`);
  };

  const handleGameClick = (gameUrl) => {
    navigate(gameUrl);
  };

  // Background image URL (set this to null or empty string to use the default color)
  const backgroundImage = "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fbg-texture-light-pink.png?alt=media&token=8b36c5d9-b7d2-4788-8ea4-19f7081bb7b3"; // Replace with actual background image URL

  const topics = [
    {
      title: "--Non Standard--",
      subtitle1: "",
      standardNumber: "PS.1.1",
      description: "Understand how forces (pushes or pulls) affect the motion of an object.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fnonstandard2025video.mp4?alt=media&token=3ca13e28-627b-4026-a3f3-0f7446b80b3c",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fnonstandard2025song.mp3?alt=media&token=e2129e68-0a2d-4d0c-9a3a-b0063dcbd98c",
      lyrics: [
        "(chorus)",
"These are my nonstandard units of measurement.",
"Just to get an idea of how big something is.",
"These are my nonstandard units of measurement.",
"Just to get an idea of how big something is.",

"(verse)",
"We can use anything to measure something.",
"We can use anything to measure something.",

"I measured my speaker and it equaled 1 sneaker.",
"I measured my chair and it equaled 5 stairs.",
"I measured my cup and it equaled 2 thumbs.",
"I measured my fan and it equaled 3 hands.",

"We can use anything to measure something.",
"We can use anything to measure something.",

"(chorus)",
"These are my nonstandard units of measurement.",
"Just to get an idea of how big something is.",
"These are my nonstandard units of measurement.",
"Just to get an idea of how big something is.",

"© Smart Board Learning, LLC. All rights reserved.",
      ],
      game: "https://example.com/game-motion",
      titleImage: "", // Motion image URL
    },
    {
      title: "--Telling Time--",
      subtitle1: "",
      standardNumber: "PS.1.1",
      description: "Understand how forces (pushes or pulls) affect the motion of an object.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Ftellingtime2025video.mp4?alt=media&token=9d27ffc5-5101-4905-821e-bf1d47cdbaeb",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Ftellingtime2025song.mp3?alt=media&token=117307c5-e509-4003-a294-7cbb49f997d4",
      lyrics: ["(chorus)",
"We can tell time, tell time, tell time.",
"We can tell time, tell time, tell time.",

"(verse)",
"Now, all clocks are broken down",
"into minutes and hours.",
"The little hand is the hour,",
"it tells us what the hour is.",
"When the little hand hits one, it’s 1 o’clock.",
"When the little hand hits two, it’s 2 o’clock.",
"When the little hand hits three, it’s 3 o’clock.",
"When the little hand hits four, it’s 4 o’clock.",

"Now, the big hand tells us the minutes.",
"Each notch is one minute.",
"Each number is five minutes.",
"When the big hand hits one, that’s 5 minutes.",
"When the big hand hits two, that’s 10 minutes.",
"When the big hand hits three, that’s 15 minutes.",
"When the big hand hits four, that’s 20 minutes.",

"(chorus)",
"We can tell time, tell time, tell time.",
"We can tell time, tell time, tell time.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "https://example.com/game-force",
      titleImage: "", // Empty string means no image for Force
    },
{
      title: "--Dollars and Cents--",
      subtitle1: "",
      standardNumber: "PS.1.1",
      description: "Understand how forces (pushes or pulls) affect the motion of an object.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fdollarsandcents2025video.mp4?alt=media&token=8454884b-b9ba-441b-9134-ca432c7319a0",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fdollarsandcents2025song.mp3?alt=media&token=97c2923e-18ea-41d1-8e22-8407ff969cc6",
      lyrics: [
        "(Chorus)",
"Make dollars, make cents.",
"Make dollars, make cents.",
"Let’s make dollars, make cents.",
"Let’s make dollars, make cents.",

"(Verse)",
"One penny is 1 cent, one penny is 1 cent.",
"The face on the front is Abraham Lincoln.",
"He was our 16th president.",
"One nickel is 5 cents, one nickel is 5 cents.",
"The face on the front is Thomas Jefferson.",
"He was our 3rd president.",
"One dime is 10 cents, one dime is 10 cents.",
"The face on the front is Franklin Roosevelt.",
"He was our 32nd president.",
"One quarter is 25 five cents, one quarter is 25 five cents.",
"The face on the front is George Washington.",
"He was our 1st president.",

"(Chorus)",
"Make dollars, make cents.",
"Make dollars, make cents.",
"Let’s make dollars, make cents.",
"Let’s make dollars, make cents.",

"(Verse)",
"Now, one penny is 1 cent, so 100 pennies is one dollar.",
"One nickel is 5 cents, so 20 nickels is one dollar.",
"One dime is 10 cents, so 10 dimes is one dollar.",
"One quarter is 25 cents, so 4 quarters is one dollar.",

"(Chorus)",
"Make dollars, make cents.",
"Make dollars, make cents.",
"Lets make dollars, make cents.",
"Lets make dollars, make cents.",

"© Smart Board Learning, LLC. All rights reserved.",
      ],
      game: "https://example.com/game-motion",
      titleImage: "", // Motion image URL
    },
    {
      title: "--Shapes--",
      subtitle1: "",
      standardNumber: "LS.1.1",
      description: "Understand the basic needs of a variety of plants and animals in different ecosystems.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fshapes2025video.mp4?alt=media&token=d30cea74-886b-46e5-93ee-4b24a068576b",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fshapes2025song.mp3?alt=media&token=3bb352c7-b9a4-46f3-91ad-f79f638945f9",
      lyrics: [
       "(chorus)",
"These are my shapes.",
"These are my shapes.",
"These are my shapes.",
"How many sides do they have?",

"(verse)",
"A triangle has 3 sides.",
"A rectangle has 4 sides.",
"A square has 4 sides.",
"A pentagon has 5 sides.",
"A hexagon has 6 sides.",
"An octagon has 8 sides.",

"(chorus)",
"These are my shapes.",
"These are my shapes.",
"These are my shapes.",
"How many sides do they have?",

"(verse)",
"A triangle has 3 sides.",
"A rectangle has 4 sides.",
"A square has 4 sides.",
"A pentagon has 5 sides.",
"A hexagon has 6 sides.",
"An octagon has 8 sides.",

"(chorus)",
"These are my shapes.",
"These are my shapes.",
"These are my shapes.",
"How many sides do they have?",

"© Smart Board Learning, LLC. All rights reserved.",
      ],
      game: "https://example.com/game-motion",
      titleImage: "", // Motion image URL
    },
    {
      title: "--Place Value--",
      subtitle1: "",
      standardNumber: "LS.1.1",
      description: "Understand the basic needs of a variety of plants and animals in different ecosystems.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fplacevalue2025video.mp4?alt=media&token=3f9c9c42-f584-47e7-bcdd-b83c8aa7fbbc",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fplacevalue2025song.mp3?alt=media&token=847038d3-72ea-4c54-9645-9ef01971073e",
      lyrics: [
       "(intro)",
"Place value.",
"Place value.",

"(chorus)",
"As we go to the left,",
"each place value goes up 10 times.",
"As we go to the left,",
"each place value goes up 10 times.",

"(verse)",
"In our number system",
"the value depends on the place or position",
"of a number, of a number.",
"In our number system",
"the value depends on the place or position",
"of a number, of a number.",

"Ones place.",
"Tens place.",
"Hundreds place.",
"Thousands place.",
"Ten thousands place.",
"Hundred thousands place.",
"Millions place.",

"Ones place.",
"Tens place.",
"Hundreds place.",
"Thousands place.",
"Ten thousands place.",
"Hundred thousands place.",
"Millions place.",

"(chorus)",
"As we go to the left,",
"each place value goes up 10 times.",
"As we go to the left,",
"each place value goes up 10 times.",

"(outro)",
"Place value.",
"Place value.",

"© Smart Board Learning, LLC. All rights reserved.",
      ],
      game: "https://example.com/game-motion",
      titleImage: "", // Motion image URL
    },
    {
      title: "--Greater Than Less Than--",
      subtitle1: "",
      standardNumber: "LS.1.1",
      description: "Understand the basic needs of a variety of plants and animals in different ecosystems.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fgreaterthan2025video.mp4?alt=media&token=5f83b245-64f5-4996-8ce7-007211772077",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fgreaterthanlessthan2025song.mp3?alt=media&token=680d1faa-4910-4495-9fe2-dcde359b0bd5",
      lyrics: [
        "(Chorus)",
"Less than, greater than, equal to, equal to.",
"Less than, greater than, equal to, equal to.",

"(Verse)",
"The greater than and less than symbol, let’s call it an alligator.",
"An alligator that’s hungry and he only eat numbers that’s greater.",
"When his mouth is open it faces the larger number.",
"So, the closed side is less than and the open side is greater.",
"If there’s an equal sign between two numbers, then they’re equal.",
"If there’s an equal sign between two numbers, then they’re equal.",

"(Chorus)",
"Less than, greater than, equal to, equal to.",
"Less than, greater than, equal to, equal to.",
"Less than, greater than, equal to, equal to.",
"Less than, greater than, equal to, equal to.",

"© Smart Board Learning, LLC. All rights reserved.",
      ],
      game: "https://example.com/game-motion",
      titleImage: "", // Motion image URL
    },
    {
      title: "--3 Time Tables Rock--",
      subtitle1: "",
      standardNumber: "LS.1.1",
      description: "Understand the basic needs of a variety of plants and animals in different ecosystems.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2F3timetablesrock2023.mp4?alt=media&token=47d85c19-fa53-412e-a361-2a392c8918c0",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2F3%20TIME%20TABLES%20ROCK.mp3?alt=media&token=92835c2f-8b53-4f7c-bef9-f9e0c244a69d",
      lyrics: ["(Chorus)",
"Let’s do our 3 timetables, let’s do our 3 timetables.",
"Rock these tables, rock these tables.",
"Let’s do our 3 timetables, let’s do our 3 timetables.",
"Rock these tables, rock these tables.",

"(Bridge)",
"3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36.",
"3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36.",
"3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36.",

"(Verse)",
"3 x 1 = 1",
"3 x 2 = 6",
"3 x 3=9",
"It’ll be 9 all the time.",
"3 x 4=12",
"3 x 5=15",
"3 x 6=18",
"Keep fresh kicks on and they stay clean.",
"3 x 7=21",
"3 x 8=24",
"3 x 9=27",
"Make the world feel like heaven.",
"3 x 10=30",
"3 x 11=33",
"3 x 12=36",
"Timetables put me in the mix.",

"(Chorus)",
"Let’s do our 3 timetables, let’s do our 3 timetables.",
"Rock these tables, rock these tables.",
"Let’s do our 3 timetables, let’s do our 3 timetables.",
"Rock these tables, rock these tables.",

"(Bridge)",
"3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36.",
"3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36.",
"3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "https://example.com/game-force",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "--6 Time Tables Rock--",
      subtitle1: "",
      standardNumber: "LS.1.1",
      description: "Understand the basic needs of a variety of plants and animals in different ecosystems.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2F6timetablesrock2023.mp4?alt=media&token=8d13212a-a66a-4db6-8425-1abe94ba01c8",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2F6%20TIME%20TABLES%20ROCK.mp3?alt=media&token=1250e76c-5cc7-4489-bc90-8c2a7b77871e",
      lyrics: ["(Bridge)",
"6, 12, 18, 24, 30, 36, 42, 48, 54, 60.",
"6, 12, 18, 24, 30, 36, 42, 48, 54, 60.",

"(Chorus)",
"6 timetables, 6 timetables.",
"6 timetables, 6 timetables.",

"(Verse)",
"6 x 1 is 6, we’re on a roll.",
"6 x 2 is 12, this we know.",
"6 x 3 is 18, get it right.",
"6 x 4 is 24, aim for the sky.",
"6 x 5 is 30, the right turns.",
"6 x 6 is 36, we learn.",
"6 x 7 is 42, right again.",
"6 x 8 is 48, my writer's pen.",
"6 x 9 is 54, stay on point.",
"6 x 10 is 60, this is my joint.",

"(Chorus)",
"6 timetables, 6 timetables.",
"6 timetables, 6 timetables.",

"(Bridge)",
"6, 12, 18, 24, 30, 36, 42, 48, 54, 60.",
"6, 12, 18, 24, 30, 36, 42, 48, 54, 60.",
"6, 12, 18, 24, 30, 36, 42, 48, 54, 60.",
"6, 12, 18, 24, 30, 36, 42, 48, 54, 60.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "https://example.com/game-force",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "--10 Time Tables Rock--",
      subtitle1: "",
      standardNumber: "LS.1.1",
      description: "Understand the basic needs of a variety of plants and animals in different ecosystems.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2F10timetablesrock2023.mp4?alt=media&token=c13bc5ae-de41-4146-9900-c04c1ba7ede6",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2F10%20TIME%20TABLES%20ROCK.mp3?alt=media&token=8af500a5-6d9e-445a-9a2d-a87e1fb4d271",
      lyrics: ["(Chorus)",
"10 times any number,",
"You put a zero after that number, you get it.",
"10 times any number,",
"You put a zero after that number, you get it.",

"(Verse 1)",
"10x1 is 10,",
"You put a zero after the 1, you get 10.",
"10x2 is 20,",
"You put a zero after the 2, you get 20.",

"(Bridge 1)",
"10, 20, 30, 40, 50 60, multiply 10’s, multiply 10’s,",
"70, 80, 90, 100, multiply 10’s, multiply 10’s.",
"10, 20, 30, 40, 50 60, multiply 10’s, multiply 10’s,",
"70, 80, 90, 100, multiply 10’s, multiply 10’s.",

"(Verse 2)",
"10x4 is 40,",
"You put a zero after the 4, you get 40.",
"10x5 is 50,",
"You put a zero after the 5, you get 50.",

"(Bridge 2)",
"10, 20, 30, 40, 50 60, multiply 10’s, multiply 10’s,",
"70, 80, 90, 100, multiply 10’s, multiply 10’s.",
"10, 20, 30, 40, 50 60, multiply 10’s, multiply 10’s,",
"70, 80, 90, 100, multiply 10’s, multiply 10’s.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "https://example.com/game-force",
      titleImage: "", // Empty string means no image for Force
    },
  ];

   // Extract all unique standardNumbers from topics in order of appearance
   const uniqueStandardNumbers = Array.from(
    new Set(topics.map(topic => topic.standardNumber))
  );

  // Map each unique standardNumber to a color based on position
  const standardNumberColorMap = {};
  uniqueStandardNumbers.forEach((number, index) => {
    const colorIndex = index % styles.topicColors.length;
    standardNumberColorMap[number] = styles.topicColors[colorIndex];
  });

  return (
    <div
      style={{
        ...styles.container,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        background: backgroundImage
          ? undefined
          : "linear-gradient(to bottom, #6dd5fa, #2980b9)", // Attractive red to pink gradient
      }}
    >
      <img
        src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f"
        alt="Logo"
        style={styles.logo}
      />
      <div style={styles.navButtons}>
        <button
          onClick={handleHomeClick}
          style={styles.homeButton} // Purple color
        >
          Home
        </button>
        <button
          onClick={() => handleSubjectClick("science")}
          style={styles.scienceButton} // Blue color
        >
          Science
        </button>
        <button
          onClick={() => handleSubjectClick("socialstudies")}
          style={styles.socialStudiesButton} // Green color
        >
          Social Studies
        </button>
        <button
          onClick={() => handleSubjectClick("math")}
          style={styles.mathButton} // Reddish color
        >
          Math
        </button>
        <button
          onClick={() => handleSubjectClick("other")}
          style={styles.otherButton} // Yellowish color
        >
          Other
        </button>
      </div>
      <h1 style={styles.title}>
  {user && user.firstName && user.lastName ? (
    <>
      North Carolina 2nd Grade Science
      <br />
      {user.sirName} {user.lastName}'s Class
    </>
  ) : (
    "North Carolina 2nd Grade Science Educator Portal"
  )}
</h1>

      <p style={styles.subtitle}></p>
      <p style={styles.subtitle}>Watch the video or listen to the song to explore new concepts,<br></br> then play the game to reinforce learning in a fun way!<br></br><br></br> Educators can request custom materials, adjust content settings,<br></br> and track student progress below.</p> 
      
     {/* Topics */}
     <div style={styles.topicsContainer}>
     {topics.map((topic, index) =>
  visibleTopics[index] && topic.title?.trim() ? (
            <div key={index} style={{ 
              ...styles.topicCard, 
              borderColor: standardNumberColorMap[topic.standardNumber] || "#ccc" 
            }}>
              <div style={styles.topicContent}>
              {topic.titleImage ? (
  <>
    <img
      src={topic.titleImage}
      alt={topic.title}
      style={styles.titleImage}
    />
    <h3 style={styles.topicTitle}>{topic.title}</h3>
  </>
) : (
  <h3 style={styles.topicTitle}>{topic.title}</h3>
)}

               {topic.subtitle1 && <p style={styles.subtitle1}>{topic.subtitle1}</p>}
                <p style={styles.subtitle2}>Topic: {index + 1}</p>   
                <p style={styles.topicDescription}>
  <span style={styles.standardNumber}>{topic.standardNumber}</span> {topic.description}
</p>

              </div>
              <div style={styles.topicButtons}>
              {topic.video && (
  <button
    style={styles.button}
    onClick={() =>
      handleOpenPopup(
        <div style={styles.mediaStage}>
          <video
            src={topic.video}
            controls
            style={styles.media}
            controlsList="nodownload noplaybackrate noremoteplayback"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
          />
        </div>
      )
    }
  >
    <img
      src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fvideo-topic-image-button2.png?alt=media&token=c787b8ef-9cfa-4051-ae09-9d58cee36987" // Example icon
      alt="Video"
      style={{ width: "20px", height: "20px", marginRight: "8px" }}
    />
    Video
  </button>
)}
{topic.music && (
  <button
    style={styles.button}
    onClick={() =>
      handleOpenPopup(
        <div>
          <audio
            src={topic.music}
            controls
            style={styles.mediaAudio}
            controlsList="nodownload noplaybackrate noremoteplayback"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
          />
          <div style={styles.lyricsContainer}>
            {topic.lyrics.map((line, idx) => (
              <p key={idx} style={styles.lyricLine}>
                {line}
              </p>
            ))}
          </div>
        </div>
      )
    }
  >
    🎵 Music
  </button>
)}
{topic.game && (
  <button style={styles.button} onClick={() => handleGameClick(topic.game)}>
    🎮 Game
  </button>
)}

              </div>
            </div>
          ) : null
        )}
      </div>

      {popupContent && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupBox}>
            <button onClick={handleClosePopup} style={styles.closeButton}>
              ✖
            </button>
            {popupContent}
          </div>
        </div>
      )}
      
      {/* Educator Controls Container */}
      {educatorControlsVisible && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupBox}>
            <button onClick={handleEducatorControlsClose} style={styles.closeButton}>
              ✖
            </button>
            <div style={styles.educatorControls}>
              <h2>Educator Controls</h2>
              <h3>Check all tools you want your student to see. Uncheck those you want to hide.</h3>
              <div style={styles.controlsGrid}>
                {topics.map((topic, index) => (
                  <div key={index} style={styles.controlItem}>
                    <label>
                      <input
                        type="checkbox"
                        checked={visibleTopics[index]}
                        onChange={() => handleCheckboxChange(index)}
                      />
                      {topic.title}
                    </label>
                  </div>
                ))}
              </div>
              <button onClick={handleSaveChanges} style={styles.saveButton}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Buttons Container */}
      <div style={styles.buttonsContainer}>
        <button style={styles.educatorControlsButton} onClick={handleEducatorControlsClick}>
          Click Here for Educator Controls
        </button>
        <button style={styles.requestButton} onClick={handleRequestClick}>
          Click Here to Request Custom Material
        </button>
        <button style={styles.studentProgressButton} onClick={handleStudentProgressClick}>
          Click Here for Student Progress
        </button>
      </div>

      {/* Form Container */}
      {formVisible && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupBox}>
            <button onClick={handleFormClose} style={styles.closeButton}>
              ✖
            </button>
            <div style={styles.formContainer}>
              <h2>Custom Material Request</h2>
              <form>
                <div style={styles.formRow}>
                  <div style={styles.formField}>
                    <label>Sir Name:</label>
                    <select style={styles.input}>
                      <option>Mr.</option>
                      <option>Mrs.</option>
                      <option>Ms.</option>
                      <option>Miss</option>
                      <option>N/A</option>
                    </select>
                  </div>
                  <div style={styles.formField}>
                    <label>First Name:</label>
                    <input type="text" style={styles.input} />
                  </div>
                  <div style={styles.formField}>
                    <label>Last Name:</label>
                    <input type="text" style={styles.input} />
                  </div>
                  <div style={styles.formField}>
                    <label>Grade:</label>
                    <select style={styles.input}>
                      <option>1st</option>
                      <option>2nd</option>
                      <option>3rd</option>
                      <option>4th</option>
                      <option>5th</option>
                    </select>
                  </div>
                  <div style={styles.formField}>
                    <label>State:</label>
                    <select style={styles.input}>
                      <option>AL</option>
                      <option>AK</option>
                      <option>AZ</option>
                      <option>AR</option>
                      <option>CA</option>
                      <option>CO</option>
                      <option>CT</option>
                      <option>DE</option>
                      <option>DC</option>
                      <option>FL</option>
                      <option>GA</option>
                      <option>HI</option>
                      <option>ID</option>
                      <option>IL</option>
                      <option>IN</option>
                      <option>IA</option>
                      <option>KS</option>
                      <option>KY</option>
                      <option>LA</option>
                      <option>ME</option>
                      <option>MD</option>
                      <option>MA</option>
                      <option>MI</option>
                      <option>MN</option>
                      <option>MS</option>
                      <option>MO</option>
                      <option>MT</option>
                      <option>NE</option>
                      <option>NV</option>
                      <option>NH</option>
                      <option>NJ</option>
                      <option>NM</option>
                      <option>NY</option>
                      <option>NC</option>
                      <option>ND</option>
                      <option>OH</option>
                      <option>OK</option>
                      <option>OR</option>
                      <option>PA</option>
                      <option>RI</option>
                      <option>SC</option>
                      <option>SD</option>
                      <option>TN</option>
                      <option>TX</option>
                      <option>UT</option>
                      <option>VT</option>
                      <option>VA</option>
                      <option>WA</option>
                      <option>WV</option>
                      <option>WI</option>
                      <option>WY</option>
                    </select>
                  </div>
                  <div style={styles.formField}>
                    <label>Subject:</label>
                    <select style={styles.input}>
                      <option>Math</option>
                      <option>Science</option>
                      <option>Social Studies</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div style={styles.formField}>
                    <label>Style:</label>
                    <select style={styles.input}>
                      <option>Select Style</option>
                      <option>Light Rock</option>
                      <option>Hip Hop</option>
                      <option>Country</option>
                      <option>Latin</option>
                      <option>R&B</option>
                      <option>Any Style</option>
                    </select>
                  </div>
                </div>
                <p></p>
                <p></p>
                <div style={styles.formField}>
                  <label>Information / Facts:</label>
                  <textarea placeholder="Please provide all facts needed in bullets for new material" style={styles.textarea}></textarea>
                </div>
                <p></p>
                <div style={styles.formField}>
                  <label>Game Questions & Answers <br></br>(All questions begin with Q-, all answers begin with A-. <br></br>The multiple choice correct answer begins with *.):</label>
                  <textarea placeholder="Please provide all games questions and answer." style={styles.textarea}></textarea>
                </div>
                <p>
                  Please allow 7-14 days for the material to be added to your portal. All custom material is protected under the Terms of Use. Payment for custom material allows the user to use the custom material within their portal until canceled.
                </p>
                <div>
                  <button type="button" onClick={() => window.open("https://example.com/pay", "_blank")} style={styles.payButton}>
                    Pay Now
                  </button>
                  <button type="button" onClick={handleFormClose} style={styles.cancelButton}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <footer style={styles.footer}>
        <p>Smart Board Learning & Freinds</p>
        <p>Trademark and copyright © 2018-2025</p>
      </footer>
    </div>
  );
};

const baseNavButton = {
  color: "#fff",
  padding: "10px 20px",
  fontSize: "25px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  width: "200px",
  height: "200px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  boxShadow: "0 6px 0 rgba(0, 0, 0, 0.4), 0 8px 10px rgba(0, 0, 0, 0.4)", // Layered shadow
  border: "2px solid #222", // Border for depth
  transition: "transform 0.2s, box-shadow 0.2s",
};

const styles = {
  container: {
    textAlign: "center",
    fontFamily: "Arial, sans-serif",
    minHeight: "100vh", // Ensure the background covers the full viewport height
    padding: "20px",
    backgroundSize: "cover", // Ensure the background image or gradient covers the entire container
    backgroundPosition: "center",
    backgroundColor: "#f0f4f8", // Softer background color
    background: "radial-gradient(circle at 30% 30%, #d0eaff, #2980b9)", // Updated gradient
  },
  footer: {
    marginTop: "40px",
    fontSize: "20px",
    color: "#000",
    textAlign: "center",
  },
  logo: {
    width: "150px",
    margin: "0 auto",
    display: "block",
    transform: "rotate(-2deg)", // Slight tilt
    filter: "drop-shadow(2px 4px 2px rgba(0,0,0,0.4))", // Drop shadow for bounce effect
  },
  navButtons: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px",
    gap: "10px",
  },
  navButton: baseNavButton, // Base style for nav buttons
  homeButton: {
    ...baseNavButton,
    backgroundColor: "#A020F0", // Purple color
  },
  scienceButton: {
    ...baseNavButton,
    backgroundColor: "#4a90e2", // Blue color
  },
  socialStudiesButton: {
    ...baseNavButton,
    backgroundColor: "#4CAF50", // Green color
  },
  mathButton: {
    ...baseNavButton,
    backgroundColor: "#FF6347", // Reddish color
  },
  otherButton: {
    ...baseNavButton,
    backgroundColor: "#FFD700", // Yellowish color
  },
  title: {
    fontSize: "35px",
    color: "#000", // Change color to black
    marginTop: "20px",
  },
  subtitle: {
    fontSize: "20px",
    color: "#555", // Softer subtitle color
    marginBottom: "20px",
  },
  topicsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "20px",
    padding: "20px",
  },
  topicCard: {
    backgroundColor: "rgba(255, 255, 255, 0.3)", // Semi-transparent white background
    borderRadius: "10px",
    boxShadow: "0 6px 10px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)", // Layered shadow for depth
    padding: "20px",
    textAlign: "center",
    backgroundImage: `
      linear-gradient(rgba(255, 255, 255, 10), rgba(255, 255, 255, 8)), 
      url('https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fmath-bg-texture-topic.png?alt=media&token=59298624-6c3d-4d1e-aaf4-dd730b6bcbc9')
    `, // Add faded overlay
    backgroundSize: "cover",
    backgroundBlendMode: "overlay", // Blend the gradient and image
    border: "5px solid", // Add border
  },
  topicTitle: {
    fontSize: "22px",
    color: "#333",
    marginBottom: "10px",
    fontFamily: "'Quicksand', sans-serif", // Playful font without cursive
  },
  topicContent: {
    marginBottom: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  titleImage: {
    width: "100%",
    maxHeight: "150px",
    objectFit: "contain",
    marginBottom: "10px",
  },
  subtitle1: {
    fontSize: "18px",
    color: "#777", // Softer subtitle color
    fontStyle: "italic",
  },
  subtitle2: {
    fontSize: "14px",
    color: "#555",
  },
  topicDescription: {
    fontSize: "14px",
    color: "#555", // Softer description color
    textAlign: "center",
  },
  topicButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
  },
  button: {
    backgroundColor: "#4a90e2", // Softer blue color
    color: "#fff",
    padding: "10px",
    fontSize: "14px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    flex: "1",
    margin: "0 5px",
    boxShadow: "0 4px 0 #1e5aa8, 0 6px 10px rgba(0,0,0,0.25)", // Blue shadow
    border: "2px solid #1e5aa8", // Blue border
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  ":hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 12px rgba(0, 0, 0, 0.4), 0 4px 6px rgba(0, 0, 0, 0.2)", // Enhanced shadow on hover
  },
  ":active": {
    transform: "translateY(0)",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.2)", // Subtle shadow on click
  },
  popupOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  popupBox: {
    // Without this a long lyric sheet pushes the box off-screen. Content
    // scrolls inside the popup now; the popup itself never exceeds the
    // viewport.
    maxHeight: "90vh",
    overflowY: "auto",
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "10px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Softer shadow
    position: "relative",
    maxWidth: "600px",
    width: "90%",
  },
  closeButton: {
    position: "absolute",
    top: "10px",
    right: "10px",
    backgroundColor: "#ff4d4d",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "30px",
    height: "30px",
    fontSize: "18px",
    cursor: "pointer",
    textAlign: "center",
    lineHeight: "30px",
  },
  // A fixed 16:9 stage. Every video renders in an identically sized frame
  // regardless of its source dimensions — a portrait clip gets black bars on
  // the sides instead of stretching the popup taller than the screen, which
  // is what forced people to scroll.
  mediaStage: {
    width: "100%",
    aspectRatio: "16 / 9",
    background: "#000",
    borderRadius: "8px",
    overflow: "hidden",
    marginTop: "10px",
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
    border: "none",
    background: "#000",
  },
  // Audio has no intrinsic height, so it keeps a simple full-width rule.
  mediaAudio: {
    width: "100%",
    marginTop: "10px",
  },
  lyricsContainer: {
    marginTop: "20px",
    textAlign: "center",
    maxHeight: "500px", // Set a maximum height
    overflowY: "auto", // Enable vertical scrolling
  },
  lyricLine: {
    fontSize: "16px",
    color: "#333",
    margin: "5px 0",
  },
  educatorControls: {
    backgroundColor: "#e0e7ef", // Softer background color for educator controls
    padding: "20px",
    borderRadius: "10px",
    marginTop: "20px",
  },
  controlsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "10px",
  },
  controlItem: {
    textAlign: "left",
  },
  formContainer: {
    backgroundColor: "#f9f9f9",
    padding: "30px",
    borderRadius: "10px",
    marginTop: "20px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 2)",
  },
  formRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "30px",
  },
  formField: {
    flex: "1 1 200",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  input: {
    width: "80%",
    padding: "10px",
    borderRadius: "5px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    border: "1px solid #ccc",
    marginTop: "5px",
  },
  textarea: {
    width: "80%",
    padding: "10",
    borderRadius: "5px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    border: "1px solid #ccc",
    marginTop: "10px",
    minHeight: "100px",
  },
  payButton: {
    backgroundColor: "#4a90e2", // Softer blue color
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 2)",
    marginTop: "20px",
  },
  requestButton: {
    backgroundColor: "#ffa500", // Orange color
    color: "#fff",
    width: "200px",
    height: "200px",
    fontSize: "20px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 #cc7000, 0 8px 10px rgba(0,0,0,0.4)", // Layered shadow
    border: "2px solid #b86e00", // Border for depth
    marginTop: "20px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: "#ff4d4d",
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 2)",
    marginTop: "20px",
    marginLeft: "10px",
  },
  educatorControlsButton: {
    backgroundColor: "#A020F0", // Purple color
    color: "#fff",
    width: "200px",
    height: "200px",
    fontSize: "20px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 #5e1280, 0 8px 10px rgba(0,0,0,0.4)", // Layered shadow
    border: "2px solid #4a0e60", // Border for depth
    marginTop: "20px",
    marginLeft: "10px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "20px",
    marginTop: "20px",
  },
  studentProgressButton: {
    backgroundColor: "#ff69b4", // Pink color
    color: "#fff",
    width: "200px",
    height: "200px",
    fontSize: "20px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 #b4004b, 0 8px 10px rgba(0,0,0,0.4)", // Layered shadow
    border: "2px solid #8e0038", // Border for depth
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "20px", // Add marginTop to align the button
  },
  studentProgress: {
    backgroundColor: "#f9f9f9",
    padding: "20px",
    borderRadius: "10px",
    marginTop: "20px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  studentButtons: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    justifyContent: "center",
  },
  studentButton: {
    backgroundColor: "#4a90e2", // Softer blue color
    color: "#000",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 2)",
  },
  saveButton: {
    backgroundColor: "linear-gradient(to bottom, #4caf50, #388e3c)", // Match button style
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 4px 0 #2e7d32, 0 6px 10px rgba(0,0,0,0.25)", // Layered shadow
    border: "2px solid #1b5e20", // Border for depth
    marginTop: "20px",
  },
  standardNumber: {
    fontWeight: "bold",
    color: "#222",
    marginRight: "5px",
  },
  topicColors: [
    "#FF6347",  // Reddish
    "#4CAF50",  // Green
    "#FFD700",  // Yellow
    "#4a90e2",  // Blue
    "#A020F0",  // Purple
    "#FF69B4",  // Pink
    "#FFA500",  // Orange
    "#2980b9",  // Deep Blue
    "#00CED1",  // Dark Turquoise (new)
    "#8B4513",  // Saddle Brown (new)
  ],
};

export default AllEducatorMath;
